import type { Store } from '../store/useStore'

/**
 * #2029 客に見せるメニューの表示。'grid' = 画像 (3 列のカード) / 'list' = リスト (写真を読まない)。
 *
 * 最初の表示は**店が選ぶ** (`store.menuLayout`、未設定は画像 = 今までどおり)。写真の少ない店は
 * リストを選べる。客は画面で切り替えられ、**店ごとに**その端末に記憶する。店をまたいで持ち越すと、
 * 写真の多い店で客が一度選んだ表示が、写真の少ない店の「最初はリスト」を上書きしてしまう
 * (逆も同じ) — 店が選んだ最初の表示を活かすため店ごとにする。
 *
 * 客が選んでいない間は店の選択に従う (店が後から変えれば、それに合わせて変わる)。
 * 記憶は端末の localStorage だけ。読み書きできない端末 (プライベートブラウズ等) は、
 * 毎回店の選択で始まるだけ。
 */
export type MenuLayout = 'grid' | 'list'

const storageKey = (storeId: string) => `menuLayout:${storeId}`

function isMenuLayout(v: unknown): v is MenuLayout {
  return v === 'grid' || v === 'list'
}

/** 店が選んだ最初の表示。未設定・知らない値は画像。 */
export function storeMenuLayout(store: Pick<Store, 'menuLayout'>): MenuLayout {
  return isMenuLayout(store.menuLayout) ? store.menuLayout : 'grid'
}

/** この端末で客がこの店について選んだ表示。選んでいなければ null。 */
export function savedMenuLayout(storeId: string): MenuLayout | null {
  try {
    const saved = localStorage.getItem(storageKey(storeId))
    return isMenuLayout(saved) ? saved : null
  } catch {
    return null
  }
}

export function rememberMenuLayout(storeId: string, layout: MenuLayout): void {
  try {
    localStorage.setItem(storageKey(storeId), layout)
  } catch {
    // 記憶できなくても今の画面は切り替わる
  }
}
